import { useState, useMemo } from 'react'
import BaseModal from '../../../../../components/BaseModal'
import { appToast } from '../../../../../components/AppToast'
import { sendBatchEmail } from '../../api/studentsApi'

const REPORT_TEMPLATES = [
  { key: 'term-1', label: 'Term 1 Report Card', subject: 'Term 1 Report Card - [Student Name]' },
  { key: 'term-2', label: 'Term 2 Report Card', subject: 'Term 2 Report Card - [Student Name]' },
  { key: 'annual', label: 'Annual Report Card', subject: 'Annual Report Card - [Student Name]' },
  { key: 'progress', label: 'Progress Report', subject: 'Progress Report - [Student Name]' },
  { key: 'custom', label: 'Custom Template', subject: '' },
]

const REPORTCardBody = `Dear Parent/Guardian,

Please find the report card for your ward [Student Name] attached.

Course: [Course]
Batch: [Batch]
Academic Year: [Year]

If you have any questions, please contact the class teacher.

Best Regards,
Examination Department`

export default function SendReportCardModal({ isOpen, onClose, students = [] }) {
  const [selectedTemplate, setSelectedTemplate] = useState('term-1')
  const [subject, setSubject] = useState(REPORT_TEMPLATES[0].subject)
  const [customMessage, setCustomMessage] = useState(REPORTCardBody)
  const [sending, setSending] = useState(false)
  const [selectedStudentIds, setSelectedStudentIds] = useState(() => new Set(students.map((s) => s.id)))

  const recipients = useMemo(() => {
    return students.filter((s) => selectedStudentIds.has(s.id) && s.email)
  }, [students, selectedStudentIds])

  const handleTemplateChange = (key) => {
    setSelectedTemplate(key)
    const tpl = REPORT_TEMPLATES.find((t) => t.key === key)
    if (tpl) setSubject(tpl.subject)
  }

  const toggleStudent = (id) => {
    setSelectedStudentIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAll = () => {
    const allEmail = students.filter((s) => s.email)
    if (selectedStudentIds.size === allEmail.length) {
      setSelectedStudentIds(new Set())
    } else {
      setSelectedStudentIds(new Set(allEmail.map((s) => s.id)))
    }
  }

  const handleSend = async () => {
    if (!subject.trim()) {
      appToast.warning('Please enter a subject line.')
      return
    }
    if (recipients.length === 0) {
      appToast.warning('No recipients with valid email addresses.')
      return
    }
    setSending(true)
    try {
      const studentIds = Array.from(selectedStudentIds)
      const res = await sendBatchEmail({
        studentIds,
        subject: subject.trim(),
        bodyHtml: customMessage.replace(/\n/g, '<br/>'),
        sendToStudent: true,
        sendToGuardian1: true,
      })
      appToast.success(res?.message || `Report card email sent to ${res?.affected_count || recipients.length} recipient(s).`)
      onClose()
    } catch (err) {
      appToast.error(err?.response?.data?.detail || 'Failed to dispatch report card emails.')
    } finally {
      setSending(false)
    }
  }

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Send Report Card Template"
      subtitle={`Bulk dispatch report cards to ${students.length} student(s) from current filter.`}
      widthClassName="max-w-2xl"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl px-5 py-2.5 text-sm font-bold text-on-surface-variant transition-colors hover:bg-surface-container-high"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || !subject.trim()}
            className="rounded-xl bg-primary px-6 py-2.5 text-sm font-bold text-on-primary shadow-sm transition-all hover:shadow-md disabled:opacity-60"
          >
            {sending ? (
              <span className="flex items-center gap-2">
                <span className="material-symbols-outlined animate-spin text-base">progress_activity</span>
                Sending...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <span className="material-symbols-outlined text-base">send</span>
                Send to {recipients.length}
              </span>
            )}
          </button>
        </>
      }
    >
      <div className="space-y-5">
        {/* Template selector */}
        <div>
          <label className="mb-1.5 block text-sm font-bold text-on-surface">Report Template</label>
          <div className="flex flex-wrap gap-2">
            {REPORT_TEMPLATES.map((tpl) => (
              <button
                key={tpl.key}
                type="button"
                onClick={() => handleTemplateChange(tpl.key)}
                className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-all ${
                  selectedTemplate === tpl.key
                    ? 'bg-primary text-on-primary shadow-sm'
                    : 'bg-surface-container-high text-on-surface-variant hover:bg-outline-variant/40'
                }`}
              >
                {tpl.label}
              </button>
            ))}
          </div>
        </div>

        {/* Subject */}
        <div>
          <label className="mb-1.5 block text-sm font-bold text-on-surface">Email Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full rounded-xl border border-outline-variant/40 bg-surface-container-low px-4 py-2.5 text-sm text-on-surface placeholder:text-outline focus:outline-none focus:ring-2 focus:ring-primary/20"
            placeholder="Enter email subject..."
          />
        </div>

        {/* Body preview */}
        <div>
          <label className="mb-1.5 block text-sm font-bold text-on-surface">Email Body</label>
          <textarea
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            rows={5}
            className="w-full resize-none rounded-xl border border-outline-variant/40 bg-surface-container-low px-4 py-3 text-sm text-on-surface placeholder:text-outline focus:outline-none focus:ring-2 focus:ring-primary/20"
            placeholder="Compose email body..."
          />
          <p className="mt-1 text-[11px] text-on-surface-variant">
            Use [Student Name], [Course], [Batch], [Year] as placeholders.
          </p>
        </div>

        {/* Recipient list */}
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <label className="text-sm font-bold text-on-surface">
              Recipients ({recipients.length})
            </label>
            <button type="button" onClick={toggleAll} className="text-xs font-bold text-primary hover:underline">
              {selectedStudentIds.size === students.filter((s) => s.email).length ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          <div className="max-h-48 space-y-1 overflow-y-auto rounded-xl border border-outline-variant/20 p-2">
            {students.filter((s) => s.email).map((student) => (
              <label
                key={student.id}
                className={`flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 transition-colors ${
                  selectedStudentIds.has(student.id) ? 'bg-primary/5' : 'hover:bg-surface-container-low'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedStudentIds.has(student.id)}
                  onChange={() => toggleStudent(student.id)}
                  className="h-4 w-4 rounded border-outline-variant text-primary focus:ring-primary/20"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-on-surface">
                    {student.firstName} {student.lastName}
                  </p>
                  <p className="text-[11px] text-on-surface-variant">
                    {student.course} &middot; {student.batch}
                  </p>
                </div>
                <span className="shrink-0 truncate text-xs text-on-surface-variant">{student.email}</span>
              </label>
            ))}
            {students.filter((s) => s.email).length === 0 && (
              <p className="py-4 text-center text-xs text-on-surface-variant">
                No students with email addresses in current filter.
              </p>
            )}
          </div>
        </div>
      </div>
    </BaseModal>
  )
}
